// Pure mutators for the multi-photo slice of a top-level DraftItem
// (#1451, epic #1449). Mirrors the `draft-box-actions.ts` contract: each
// function takes the current scratchpad and returns the next one, or
// `null` when the scratchpad is absent (the store callback then returns
// the existing state unchanged). The functions never mutate their input.
//
// WHY these live outside `store.ts`: the SIMPLE editor appends/removes
// photo keys per item while `store.ts` is already near the hand-written
// file-size cap. The store keeps only thin wrappers; the array logic
// (cap, dedup, immutability) lives here so it stays unit-testable.

import { REQUEST_ITEM_PHOTOS_MAX } from '@magimp/shared';
import type { DraftScratchpad } from './types.js';

/**
 * Append `key` to the matching item's `photos`. No-op (returns the
 * input scratchpad unchanged) when `d` is null, the item is not found,
 * the item already holds `REQUEST_ITEM_PHOTOS_MAX` photos, or `key` is
 * already present. Never mutates the input.
 *
 * @aiInvariant photo order is upload order; appends always go to the end
 *   so the first key stays the primary thumbnail (#1451 D2/D6).
 */
export function addItemPhoto(
  d: DraftScratchpad | null,
  itemId: string,
  key: string,
): DraftScratchpad | null {
  if (d === null) return null;
  return {
    ...d,
    items: d.items.map((it) => {
      if (it.id !== itemId) return it;
      if (it.photos.length >= REQUEST_ITEM_PHOTOS_MAX) return it;
      if (it.photos.includes(key)) return it;
      return { ...it, photos: [...it.photos, key] };
    }),
  };
}

/**
 * Remove `key` from the matching item's `photos`. No-op when `d` is
 * null, the item is not found, or `key` is absent. Never mutates the
 * input. The removed key is NOT eagerly deleted from storage — the
 * existing discard / save-commit cleanup (driven by `unsavedPhotoKeys`)
 * releases an orphaned object on the next close or save.
 */
export function removeItemPhoto(
  d: DraftScratchpad | null,
  itemId: string,
  key: string,
): DraftScratchpad | null {
  if (d === null) return null;
  return {
    ...d,
    items: d.items.map((it) =>
      it.id === itemId ? { ...it, photos: it.photos.filter((p) => p !== key) } : it,
    ),
  };
}
